import { useGuardedAction } from "../shared/UnsavedChangesGuard";
import PageHeader from "../shared/PageHeader";
import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  useMediaQuery,
  ThemeProvider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AppBarComponent from "./appBarComponent";
import DrawerComponent from "./drawerComponent";
import MenuItems from "./menuItems";
import AuthFormComponent from "./authFormComponent";
import ContentRenderer from "./contentRender";
import { useSelector } from "react-redux";
import { selectUnseenCount } from "../../redux/slice/sseSlicer";
import useOrders from "../orderTable/fetchedOrders";
import useQrOrderSSE from "../qrMenu/qrOrderTable/useQrOrderSEE";
import theme from "../../theme/adminTheme";

const PermanentDrawerLeft = () => {
  const request = useGuardedAction();
  const [open, setOpen] = useState(false);
  const [orderPage, setOrderPage] = useState(null);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [kitchenExpanded, setKitchenExpanded] = useState(false);
  const [kitchenPage, setKitchenPage] = useState("online");
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [billingPage, setBillingPage] = useState("restaurant");
  const [selectedItem, setSelectedItem] = useState("Item Management");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const { data: onlineOrders = [], newOrderCount, resetOrderCount } = useOrders();
  const kitchenOrderCount = (Array.isArray(onlineOrders) ? onlineOrders : []).filter(order => String(order.track_status || "").replace(/"/g, "").trim().toLowerCase() === "accepted").length;
  const { newQrOrderCount, resetQrOrderCount } = useQrOrderSSE();
  const unseenCount = useSelector(selectUnseenCount);
  const unreadOrders = useSelector((state) => state.waiter.unreadOrders);
  const [visitedSections, setVisitedSections] = useState(() => {
    const storedVisited = localStorage.getItem("visitedSections");
    return storedVisited
      ? JSON.parse(storedVisited)
      : {
          order: newOrderCount === 0, // Mark as visited if no new orders
          kitchen: unreadOrders === 0, // Mark as visited if no unread orders
          qrOrder: newQrOrderCount === 0,
        };
  });
  const navigate = useNavigate();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const storedAuthState = localStorage.getItem("authState");
    if (storedAuthState) {
      const {
        isAuthenticated,
        role,
        adminData,
        visitedSections: storedVisited,
      } = JSON.parse(storedAuthState);
      setIsAuthenticated(isAuthenticated);
      setRole(role);
      setAdminData(adminData);
      if (storedVisited) {
        setVisitedSections(storedVisited);
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleItemClick = (item) => {
    setSelectedItem(item);

    if (item === "Order Management") {
      setOrdersExpanded(current => !current);
      setVisitedSections((prev) => ({ ...prev, order: true }));
      resetOrderCount(); // Call this to reset the count when visiting orders
    } else if (item === "Kitchen") {
      setKitchenExpanded(current => !current);
      setVisitedSections((prev) => ({ ...prev, kitchen: true }));
    } else if (item === "Billing Report") {
      setBillingExpanded(current => !current);
    }
    if (item === "Qr Order") {
      setVisitedSections((prev) => ({ ...prev, qrOrder: true }));
      resetQrOrderCount(); // ✅ IMPORTANT
    }

    if (isSmallScreen && item !== "Order Management") {
      setOpen(false);
    }
  };

  useEffect(() => {
    setVisitedSections((prev) => ({
      ...prev,
      order: newOrderCount === 0,
      kitchen: unreadOrders === 0,
      qrOrder: newQrOrderCount === 0,
    }));
  }, [newOrderCount, unreadOrders,newQrOrderCount]);

  useEffect(() => {
    localStorage.setItem("visitedSections", JSON.stringify(visitedSections));
  }, [visitedSections]);

  const handleAuthSubmit = ({ isAuthenticated, role, adminData }) => {
    setIsAuthenticated(isAuthenticated);
    setRole(role);
    setAdminData(adminData);
    localStorage.setItem(
      "authState",
      JSON.stringify({
        isAuthenticated,
        role,
        selectedItem,
        adminData,
        visitedSections,
      })
    );
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setRole(null);
    setAdminData(null);
    localStorage.removeItem("authState");
    localStorage.removeItem("adminData");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    navigate("/");
  };

  if (!isAuthenticated) {
    return <AuthFormComponent onAuthSubmit={handleAuthSubmit} />;
  }

  const menuItems = MenuItems({
    role,
    unreadOrders,
    selectedItem,
    visitedSections,
    adminData,
    unseenCount,
    newOrderCount,
    newQrOrderCount,
    kitchenOrderCount,
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          minHeight: "100vh",
        }}
      >
        <CssBaseline />
        <a className="skip-link" href="#main-content">Skip to content</a>
        <AppBarComponent
          open={open}
          onDrawerOpen={() => setOpen(true)}
          onLogout={() => request(handleLogout)}
          user={adminData}
          role={role}
          onNotificationSelect={destination => request(() => { setSelectedItem(destination === "kitchen" ? "Kitchen" : "Order Management"); if (destination === "kitchen") { setKitchenExpanded(true); setKitchenPage("online"); } else { setOrdersExpanded(true); setOrderPage("OrderDetail"); } })}
        />
        <DrawerComponent
          open={open}
          onDrawerClose={() => setOpen(false)}
          menuItems={menuItems}
          selectedItem={selectedItem}
          onItemSelect={item => item === selectedItem ? handleItemClick(item) : request(() => handleItemClick(item))}
          adminData={adminData}
          role={role}
          ordersExpanded={ordersExpanded}
          kitchenExpanded={kitchenExpanded}
          kitchenPage={kitchenPage}
          onKitchenPageSelect={page => { setSelectedItem("Kitchen"); setKitchenPage(page); setVisitedSections(prev => ({ ...prev, kitchen: true })); if (isSmallScreen) setOpen(false); }}
          billingExpanded={billingExpanded}
          billingPage={billingPage}
          onBillingPageSelect={page => { setSelectedItem("Billing Report"); setBillingPage(page); if (isSmallScreen) setOpen(false); }}
          orderPage={orderPage}
          onOrderPageSelect={page => request(() => { setSelectedItem("Order Management"); setOrderPage(page); setVisitedSections(prev => ({ ...prev, order: true })); resetOrderCount(); if (isSmallScreen) setOpen(false); })}
        />
        <Box component="main" id="main-content" tabIndex={-1} sx={{ flexGrow: 1, minWidth: 0, width: "100%", pt: { xs: 11, md: 13 }, px: { xs: 2, md: 4 }, pb: 4 }}>
          <PageHeader title={selectedItem} breadcrumbs={["Workspace", selectedItem]} />
          <ContentRenderer selectedItem={selectedItem} role={role} orderPage={orderPage} onOrderPageChange={page => request(() => setOrderPage(page))} kitchenPage={kitchenPage} billingPage={billingPage} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PermanentDrawerLeft;
